/**
 * HoverTip — 짧은 보조 텍스트용 범용 툴팁.
 *
 * - portal(document.body) 렌더라 부모의 overflow:hidden/transform에 잘리지 않음
 *   (KPI 카드 `.widget-tile`이 overflow:hidden이라 CSS ::after 툴팁은 잘림 → 이 컴포넌트 필요)
 * - 데스크탑: 마우스 hover/focus 로 표시
 * - 모바일(터치): 탭으로 토글 (hover가 없으므로)
 * - 스크롤/리사이즈/바깥 탭 시 자동 닫힘
 *
 * 사용:
 *   <HoverTip tip="전월 대비" className="kpi-delta down">▼ −46.8%</HoverTip>
 *   className 은 anchor(감싸는 span)에 그대로 부여됨 → 기존 칩 스타일 재사용 가능.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

interface Props {
  tip: ReactNode;
  children: ReactNode;
  className?: string;
  /** 여러 줄/단락 안내문(개행 `\n` 포함)일 때 — 줄바꿈 허용 + 넓은 박스 */
  wide?: boolean;
  /** 툴팁 박스에 덧붙일 클래스 — 카드형 커스텀 툴팁을 만들 때 */
  tipClassName?: string;
}

interface TipPos {
  x: number;
  y: number;
  below: boolean;
}

export function HoverTip({ tip, children, className, wide, tipClassName }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<TipPos | null>(null);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = r.top < 44; // 위쪽 공간 부족하면 아래로 flip
    setPos({ x: r.left + r.width / 2, y: below ? r.bottom : r.top, below });
  }, []);
  const hide = useCallback(() => setPos(null), []);
  const toggle = useCallback(() => { if (pos) hide(); else show(); }, [pos, hide, show]);

  // 열려 있는 동안: 스크롤/리사이즈/바깥 포인터다운 시 닫기
  useEffect(() => {
    if (!pos) return;
    const onScroll = () => hide();
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) hide();
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [pos, hide]);

  return (
    <span
      ref={ref}
      className={className}
      style={{ cursor: "help" }}
      tabIndex={0}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") show(); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") hide(); }}
      onFocus={show}
      onBlur={hide}
      onClick={(e) => { e.stopPropagation(); toggle(); }}
    >
      {children}
      {pos && typeof document !== "undefined"
        ? createPortal(
            <div
              className={`hovertip${wide ? " hovertip--wide" : ""}${tipClassName ? ` ${tipClassName}` : ""}`}
              role="tooltip"
              style={{
                left: pos.x,
                top: pos.y,
                transform: `translate(-50%, ${pos.below ? "6px" : "calc(-100% - 6px)"})`,
              }}
            >
              {tip}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
